import { useState, useCallback } from 'react';
import { Upload, Search, FileText, Loader } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import VoiceInput from '../components/common/VoiceInput';
import { uploadDocument, searchDocuments } from '../services/api';

export default function DocumentsPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const data = await searchDocuments(query);
      setResults(data.results || data.documents || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleUpload = useCallback(async (files: FileList) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadDocument(file, 'kog_sales');
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      handleUpload(e.dataTransfer.files);
    }
  }, [handleUpload]);

  return (
    <div className="page-enter">
      <Header title="Documents" subtitle="Upload and search your knowledge base" />

      <div className="p-4 md:p-8 space-y-6">
        {/* Upload zone */}
        <div
          className="rounded-xl p-8 text-center cursor-pointer transition-all duration-200"
          style={{
            background: dragOver ? 'var(--bg-hover)' : 'var(--bg-panel)',
            border: `2px dashed ${dragOver ? 'var(--netrun-green)' : 'var(--border-subtle)'}`,
            animation: dragOver ? 'pulse-sage 1.5s infinite' : 'none',
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = '.pdf,.doc,.docx,.txt,.csv';
            input.onchange = (e) => {
              const files = (e.target as HTMLInputElement).files;
              if (files) handleUpload(files);
            };
            input.click();
          }}
        >
          {uploading ? (
            <Loader size={32} className="mx-auto mb-3 animate-spin" style={{ color: 'var(--netrun-green)' }} />
          ) : (
            <Upload size={32} className="mx-auto mb-3" style={{ color: 'var(--border-subtle)' }} />
          )}
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            PDF, DOCX, TXT, CSV — auto-indexed into kog_sales collection
          </p>
        </div>

        {/* Semantic search */}
        <Panel title="Semantic Search">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }} />
              <VoiceInput
                value={query}
                onChange={setQuery}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search your knowledge base..."
                className="w-full pl-10 py-3 rounded-lg text-sm outline-none"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-primary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-6 py-3 rounded-lg text-sm font-body cursor-pointer transition-all"
              style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', border: 'none' }}
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Results */}
          {query.trim() && !searching && results.length === 0 && (
            <div className="mt-8 flex flex-col items-center justify-center py-8">
              <Search size={40} style={{ color: 'var(--border-subtle)', marginBottom: '12px' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                No results found for "{query}"
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Try different keywords or upload more documents
              </p>
            </div>
          )}
          {results.length > 0 && (
            <div className="mt-4 space-y-3">
              {results.map((result: any, i: number) => {
                const score = result.score ?? result.distance ?? result.relevance;
                const relevancePct = score != null ? Math.round(Math.min(score, 1) * 100) : null;
                return (
                  <div key={i} className="rounded-lg p-4 relative overflow-hidden"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)' }}>
                    {/* Relevance indicator bar */}
                    {relevancePct != null && (
                      <div
                        className="absolute top-0 left-0 h-[3px] rounded-br"
                        style={{
                          width: `${relevancePct}%`,
                          background: relevancePct > 70 ? 'var(--netrun-green)' : relevancePct > 40 ? 'var(--warning)' : 'var(--text-muted)',
                        }}
                      />
                    )}
                    <div className="flex items-start gap-3">
                      <FileText size={16} className="mt-0.5 flex-shrink-0"
                        style={{ color: 'var(--netrun-green)' }} />
                      <div className="flex-1">
                        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {result.document || result.text || result.content}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          {result.metadata && (
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              Source: {result.metadata.source || result.metadata.filename || 'Unknown'}
                            </p>
                          )}
                          {relevancePct != null && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded font-body"
                              style={{
                                background: relevancePct > 70 ? 'rgba(144,185,171,0.15)' : 'rgba(251,191,36,0.15)',
                                color: relevancePct > 70 ? 'var(--netrun-green)' : 'var(--warning)',
                              }}
                            >
                              {relevancePct}% match
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
